import type PerformerModel from "parog-web/models/performer";
import { performerRetrieve } from "parog-web/utils/dmp-utils";
import type {
  CodeAttributes,
  Entry,
  Execution,
  Performer,
  Section
} from "./abstract-dmp-parser";
import AbstractDmpParser from "./abstract-dmp-parser";

interface Encounter {
  code: {
    _attributes: CodeAttributes;
    qualifier: {
      value: {
        _attributes: CodeAttributes;
      };
    };
  };
  effectiveTime: {
    low: {
      _attributes: {
        value: unknown;
      };
    };
    high: {
      _attributes: {
        value: unknown;
      };
    };
  };
  performer: Performer;
}

interface EncounterEntry extends Entry {
  encounter: Encounter;
}

interface HospitalisationExecution extends Execution {
  visitStartDate: Date;
  visitEndDate?: Date;
  performer: PerformerModel;
}

export default class HospitalisationDmpParser extends AbstractDmpParser {
  journalEventClassName = "HospitalisationRefundedJournalEvent";

  canParse(section: Section): boolean {
    const code = section.code?._attributes?.code;
    const title = section.title?._text;
    return code == "46240-8" && title == "Hospitalisations";
  }

  parse(section: Section): Array<HospitalisationExecution> {
    return (this.getEntries(section) as Array<EncounterEntry>)
      .map(entry => {
        const encounter = entry.encounter;

        if (
          encounter.code?._attributes?.nullFlavor ||
          encounter.code?._attributes?.codeSystem == "1.2.250.1.213.2.63"
        )
          return;

        const visitStartDate = this.transformStringToDate(
          encounter.effectiveTime?.low?._attributes?.value as string | undefined
        );
        const visitEndDate =
          this.transformStringToDate(
            encounter.effectiveTime?.high?._attributes?.value as
              | string
              | undefined
          ) || undefined;

        const source = encounter.code?.qualifier?.value
          ?._attributes as Encounter["code"]["qualifier"]["value"]["_attributes"];
        if (!source) return;

        let visitSourceConcept;
        if (source.codeSystem == "1.2.250.1.215.200.3.1") {
          visitSourceConcept = "GHS";
        } else if (source.codeSystem == "1.2.250.1.215.200.3.2") {
          visitSourceConcept = "DDP";
        } else if (source.codeSystem == "1.2.250.1.215.200.3.3") {
          visitSourceConcept = "NGAP";
        }
        const visitSourceValue = source.code;
        const visitSourceLabel = source.displayName;

        const occurredAt = this.parseOccurredAt(
          encounter,
          "effectiveTime.low._attributes.value"
        );

        if (
          visitStartDate &&
          visitSourceConcept &&
          visitSourceValue &&
          visitSourceLabel &&
          occurredAt
        ) {
          return {
            occurredAt,
            visitStartDate,
            visitEndDate,
            visitSourceConcept,
            visitSourceValue,
            visitSourceLabel,
            performer: performerRetrieve(entry.encounter?.performer)
          };
        }

        return undefined;
      })
      .filter(Boolean) as HospitalisationExecution[];
  }
}
